import styled from '@emotion/styled';
import { BREAKPOINTS, media } from '../../styles/constants';

const landingSectionPaddingY = `
  padding-top: 88px;
  padding-bottom: 88px;

  ${media.max(820)} {
    padding-top: 64px;
    padding-bottom: 64px;
  }
`;

export const LandingPageRoot = styled.div`
  width: 100%;
  align-self: stretch;
  min-height: 100%;
  background-color: ${({ theme }) => theme.palette.background.default};
  color: ${({ theme }) => theme.palette.text.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily};

  ${media.min(BREAKPOINTS.tabletMin)} {
    padding-top: var(--landing-header-height, 0px);
  }
`;

export const LandingContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px;
  box-sizing: border-box;

  ${media.max(820)} {
    padding: 0 20px;
  }
`;

export const LandingHeaderShell = styled.div`
  width: 100%;
  z-index: 200;
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.palette.background.lightGray};
  color: ${({ theme }) => theme.palette.text.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  border-bottom: 1px solid
    ${({ $scrolled, theme }) => ($scrolled ? theme.custom.grayBorder : 'transparent')};
  box-shadow: ${({ $scrolled }) => ($scrolled ? '0 4px 14px rgba(0, 0, 0, 0.06)' : 'none')};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  ${({ $fixed }) =>
    $fixed
      ? `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
  `
      : ''}
`;

export const LandingHeaderSpacer = styled.div`
  display: none;
`;

export const LandingHeaderBar = styled.header`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 10px 16px;
  padding: 10px 0 8px;
  
  ${media.max(820)} {
    padding: 8px 0 6px;
    gap: 8px 10px;
  }
`;

export const HeaderBrandWrap = styled.div`
  flex-shrink: 0;

  & > a,
  & > a > div,
  & > div {
    align-items: flex-start;
    align-self: flex-start;
    margin: 0;
    width: auto;
    max-width: none;
  }
`;

export const HeaderNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 36px;
  margin-left: auto;

  ${media.max(820)} {
    order: 3;
    width: 100%;
    margin-left: 0;
    justify-content: center;
    gap: 10px;
    flex-wrap: wrap;
    padding-top: 4px;
  }
`;

export const HeaderNavLink = styled.a`
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.text.primary};
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.palette.primary.main};
  }

  ${media.max(820)} {
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 400;
    background-color: ${({ theme }) => theme.custom.white};
    color: ${({ theme }) => theme.palette.text.primary};
    border: 1px solid ${({ theme }) => theme.custom.grayInputs};

    &:hover {
      background-color: #e8f5e9;
      color: ${({ theme }) => theme.palette.text.primary};
    }
  }

  ${media.max(480)} {
    padding: 8px 14px;
    font-size: 13px;
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  margin-left: auto;

  ${media.max(820)} {
    gap: 10px;
  }
`;

export const HeaderLoginButton = styled.button`
  height: 38px;
  padding: 8px 22px;
  border: none;
  border-radius: 30px;
  background-color: ${({ theme }) => theme.palette.primary.main};
  color: ${({ theme }) => theme.custom.white};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    opacity: 0.92;
  }

  ${media.max(820)} {
    height: 36px;
    padding: 6px 16px;
    font-size: 13px;
  }
`;

export const HeroSection = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  padding: 40px 0 80px;
  min-height: calc(var(--vh, 1vh) * 100 - var(--landing-header-height, 72px));
  box-sizing: border-box;

  ${media.max(820)} {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 32px;
    padding: 64px 0;
    min-height: auto;
  }
`;

export const HeroContent = styled.div`
  flex: 1;
  max-width: 580px;
  display: flex;
  flex-direction: column;
  gap: 28px;

  ${media.max(820)} {
    max-width: none;
    width: 100%;
    align-items: center;
    gap: 20px;
  }
`;

export const HeroTitle = styled.h2`
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 80px;
  font-weight: 700;
  line-height: 1.08;
  color: ${({ theme }) => theme.palette.primary.main};
  text-align: left;

  ${media.max(1024)} {
    font-size: 56px;
  }

  ${media.max(820)} {
    font-size: 57px;
    line-height: 1.12;
    text-align: center;
    align-items: center;
  }

  ${media.max(480)} {
    font-size: 48px;
  }
`;

export const HeroDescription = styled.p`
  margin: 0;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.6;
  color: ${({ theme }) => theme.palette.text.primary};

  ${media.max(820)} {
    font-size: 16px;
    line-height: 1.55;
    max-width: 480px;
  }
`;

export const StoreBadgesRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;

  ${media.max(820)} {
    align-items: center;
  }
`;

export const StoreButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
`;

export const StoreBadgeLink = styled.a`
  display: block;
  line-height: 0;
  text-decoration: none;
`;

export const StoreBadge = styled.img`
  height: 44px;
  width: auto;
  display: block;

  ${media.max(480)} {
    height: 40px;
  }
`;

export const HeroVisual = styled.div`
  flex: 0 1 auto;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  max-width: 480px;

  ${media.max(820)} {
    max-width: 100%;
    width: 100%;
    justify-content: center;
  }
`;

export const HeroImage = styled.img`
  width: 100%;
  max-width: 500px;
  height: auto;
  display: block;

  ${media.max(820)} {
    max-width: min(320px, 88vw);
  }

  ${media.max(480)} {
    max-width: min(280px, 90vw);
  }
`;

export const FeatureCareSection = styled.section`
  width: 100%;
  background-color: ${({ theme }) => theme.palette.primary.main};
  padding: 88px 24px;
  box-sizing: border-box;

  ${media.max(820)} {
    padding: 64px 20px;
  }
`;

export const FeatureCareInner = styled.div`
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 48px;

  ${media.max(820)} {
    gap: 36px;
  }
`;

export const FeatureCareSliderWrap = styled.div`
  width: 100%;
  max-width: 460px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const FeatureCareTitle = styled.h3`
  margin: 0;
  font-size: 48px;
  font-weight: 700;
  line-height: 1.2;
  color: ${({ theme }) => theme.custom.white};

  ${media.max(820)} {
    font-size: 28px;
  }

  ${media.max(480)} {
    font-size: 24px;
  }
`;

export const FeatureScreensSection = styled.section`
  width: 100%;
  background-color: ${({ theme }) => theme.palette.background.lightGray};
  ${landingSectionPaddingY}
  box-sizing: border-box;
`;

export const FeatureScreensTitle = styled.h2`
  margin: 0 0 48px;
  font-size: 36px;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  color: ${({ theme }) => theme.palette.text.primary};

  ${media.max(820)} {
    font-size: 30px;
    margin-bottom: 32px;
  }

  ${media.max(480)} {
    font-size: 26px;
  }
`;

export const FeatureScreensGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 48px 32px;

  ${media.max(1024)} {
    gap: 40px 24px;
  }

  ${media.between(600, 819)} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  ${media.max(599)} {
    grid-template-columns: 1fr;
    gap: 36px;
    max-width: 280px;
    margin: 0 auto;
  }
`;

export const FeatureScreenCard = styled.article`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 20px;
`;

export const FeatureScreenImageWrap = styled.div`
  width: 100%;
  max-width: 220px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  min-height: 300px;

  ${media.max(820)} {
    max-width: 200px;
    min-height: 260px;
  }

  ${media.max(599)} {
    max-width: 220px;
    min-height: 280px;
  }
`;

export const FeatureScreenImageButton = styled.button`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  cursor: zoom-in;
  border-radius: 12px;
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.02);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.primary.main};
    outline-offset: 4px;
  }
`;

export const FeatureScreenLightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 56px 20px;
  box-sizing: border-box;
  background-color: rgba(0, 0, 0, 0.75);
  cursor: zoom-out;
`;

export const FeatureScreenLightboxStage = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: min(92vw, 420px);
  max-height: min(88vh, 760px);
  cursor: default;
`;

export const FeatureScreenLightboxClose = styled.button`
  position: absolute;
  top: -44px;
  right: 0;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 24px;
    height: 24px;
    filter: brightness(0) invert(1);
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
    border-radius: 4px;
  }

  ${media.max(480)} {
    top: -40px;
    right: -4px;
  }
`;

export const FeatureScreenLightboxInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

export const FeatureScreenLightboxImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  max-height: min(88vh, 760px);
  object-fit: contain;
  border-radius: 16px;
`;

export const FeatureScreenImage = styled.img`
  width: 100%;
  height: auto;
  max-height: 320px;
  object-fit: contain;
  display: block;
  filter: drop-shadow(0 12px 28px rgba(0, 0, 0, 0.1));

  ${media.max(820)} {
    max-height: 280px;
  }
`;

export const FeatureScreenCaption = styled.p`
  margin: 0;
  max-width: 240px;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.45;
  color: ${({ theme }) => theme.palette.text.primary};

  ${media.max(820)} {
    font-size: 15px;
    max-width: 220px;
  }
`;

export const ExportRecordsSection = styled.section`
  width: 100%;
  background-color: ${({ theme }) => theme.palette.primary.main};
  ${landingSectionPaddingY}
  box-sizing: border-box;
  overflow: hidden;
`;

export const ExportRecordsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;

  ${media.max(820)} {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 32px;
  }
`;

export const ExportRecordsContent = styled.div`
  flex: 1;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  ${media.max(820)} {
    max-width: 520px;
    align-items: center;
  }
`;

export const ExportRecordsTitle = styled.h3`
  margin: 0;
  font-size: 48px;
  font-weight: 700;
  line-height: 1.15;
  color: ${({ theme }) => theme.custom.white};
  text-align: left;

  ${media.max(1024)} {
    font-size: 36px;
  }

  ${media.max(820)} {
    font-size: 28px;
    text-align: center;
  }

  ${media.max(480)} {
    font-size: 24px;
  }
`;

export const ExportRecordsSubtitle = styled.p`
  margin: 0;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.55;
  color: ${({ theme }) => theme.custom.white};
  text-align: left;

  ${media.max(820)} {
    font-size: 16px;
    text-align: center;
    max-width: 360px;
  }
`;

export const ExportRecordsVisual = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  max-width: 520px;

  ${media.max(820)} {
    max-width: 100%;
    width: 100%;
    justify-content: center;
  }
`;

export const ExportRecordsImage = styled.img`
  width: 100%;
  max-width: 480px;
  height: auto;
  display: block;

  ${media.max(820)} {
    max-width: min(360px, 92vw);
  }

  ${media.max(480)} {
    max-width: min(300px, 94vw);
  }
`;

export const LandingFaqSection = styled.section`
  width: 100%;
  background-color: ${({ theme }) => theme.palette.background.default};
  ${landingSectionPaddingY}
  box-sizing: border-box;
`;

export const LandingFaqInner = styled.div`
  width: 100%;
  max-width: 860px;
  margin: 0 auto;
  padding: 0 40px;
  box-sizing: border-box;

  ${media.max(820)} {
    padding: 0 20px;
  }
`;

export const LandingFaqTitle = styled.h2`
  margin: 0 0 40px;
  font-size: 36px;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  color: ${({ theme }) => theme.palette.text.primary};

  ${media.max(820)} {
    font-size: 30px;
    margin-bottom: 28px;
  }

  ${media.max(480)} {
    font-size: 26px;
  }
`;

export const ContactsSection = styled.section`
  width: 100%;
  background-color: ${({ theme }) => theme.palette.primary.main};
  ${landingSectionPaddingY}
  box-sizing: border-box;
  overflow: hidden;
`;

export const ContactsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 48px;

  ${media.max(820)} {
    flex-direction: column;
    align-items: stretch;
    gap: 32px;
  }
`;

export const ContactsContent = styled.div`
  flex: 1;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  color: ${({ theme }) => theme.custom.white};

  ${media.max(820)} {
    max-width: none;
    text-align: center;
    align-items: center;
  }
`;

export const ContactsTitle = styled.h2`
  margin: 0;
  font-size: 48px;
  font-weight: 700;
  line-height: 1.15;
  color: ${({ theme }) => theme.custom.white};

  ${media.max(820)} {
    font-size: 30px;
  }

  ${media.max(480)} {
    font-size: 26px;
  }
`;

export const ContactsDescription = styled.p`
  margin: 0;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.55;
  color: ${({ theme }) => theme.custom.white};

  ${media.max(820)} {
    font-size: 16px;
    max-width: 520px;
  }
`;

export const ContactsEmailLink = styled.a`
  font-size: 18px;
  font-weight: 500;
  color: ${({ theme }) => theme.custom.white};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  ${media.max(820)} {
    font-size: 16px;
  }
`;

export const ContactsFormCard = styled.div`
  flex: 1;
  max-width: 520px;
  width: 100%;
  background-color: ${({ theme }) => theme.custom.white};
  border-radius: 20px;
  padding: 28px 24px;
  box-sizing: border-box;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${media.max(820)} {
    max-width: none;
  }
`;

export const ContactsFormTitle = styled.h3`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  line-height: 1.25;
  color: ${({ theme }) => theme.palette.text.primary};
  text-align: left;

  ${media.max(820)} {
    font-size: 20px;
  }
`;

export const DeleteAccountCardParagraph = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.55;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const DeleteAccountStepsList = styled.ol`
  margin: 0;
  padding-left: 1.35em;
  list-style-position: outside;

  & > li {
    list-style-type: decimal;
  }

  &[data-variant='bullet'] > li {
    list-style-type: disc;
  }
`;

export const DeleteAccountStep = styled.li`
  display: list-item;
  font-size: 16px;
  line-height: 1.5;
  color: ${({ theme }) => theme.palette.text.primary};
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const DeleteAccountInlineLink = styled.a`
  color: ${({ theme }) => theme.palette.primary.main};
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export const LandingFooter = styled.footer`
  width: 100%;
  background-color: ${({ theme }) => theme.palette.background.lightGray};
  box-sizing: border-box;
`;

export const LandingFooterMain = styled.div`
  padding: 48px 0 32px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    'logo .'
    'content links';
  column-gap: 48px;
  row-gap: 16px;
  align-items: start;

  ${media.max(820)} {
    grid-template-columns: 1fr;
    grid-template-areas:
      'logo'
      'content'
      'links';
    gap: 24px;
    padding: 40px 0 28px;
  }
`;

export const FooterLogoCell = styled.div`
  grid-area: logo;
  justify-self: start;
`;

export const FooterBrandColumn = styled.div`
  grid-area: content;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;

  ${media.max(820)} {
    max-width: none;
  }
`;

export const FooterDescription = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.55;
  color: ${({ theme }) => theme.palette.text.primary};
  max-width: 100%;
  width: 100%;

  ${media.max(820)} {
    font-size: 15px;
  }
`;

export const FooterLinksColumn = styled.div`
  grid-area: links;
  flex-shrink: 0;
  min-width: 180px;
  display: flex;
  flex-direction: column;
  align-self: start;

  ${media.max(820)} {
    width: 100%;
  }
`;

export const FooterLinksList = styled.nav`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`;

export const FooterLink = styled.a`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.4;
  color: ${({ theme }) => theme.palette.text.primary};
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;

export const FooterLoginButton = styled(HeaderLoginButton)`
  margin-top: 16px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const LandingFooterBottom = styled.div`
  border-top: 1px solid ${({ theme }) => theme.custom.grayBorder};
  padding: 20px 0 28px;
  text-align: center;
`;

export const FooterCopyright = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const LegalPageSection = styled.main`
  width: 100%;
  padding: 48px 0 64px;
  box-sizing: border-box;

  ${media.max(820)} {
    padding: 36px 0 48px;
  }
`;

export const LegalPageTitle = styled.h1`
  margin: 0 0 24px;
  font-size: 40px;
  font-weight: 700;
  line-height: 1.15;
  color: ${({ theme }) => theme.palette.text.primary};

  ${media.max(820)} {
    font-size: 30px;
    margin-bottom: 20px;
  }

  ${media.max(480)} {
    font-size: 26px;
  }
`;

export const LegalPageContentCard = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.palette.background.lightGray};
  border-radius: 12px;
  padding: 28px 24px;
  box-sizing: border-box;
  text-align: left;
  font-size: 16px;
  line-height: 1.6;
  color: ${({ theme }) => theme.palette.text.primary};

  ${media.max(820)} {
    padding: 20px 16px;
    font-size: 15px;
  }
`;

export const LoginPageRoot = styled(LandingPageRoot)`
  box-sizing: border-box;
  flex: 1 0 auto;
  width: 100%;
  min-height: calc(var(--vh, 1vh) * 100);
  display: flex;
  flex-direction: column;
`;

export const LoginPageFill = styled(LandingContainer)`
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

export const LoginPageSection = styled.main`
  width: 100%;
  padding: 48px 0 72px;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  ${({ $fill }) =>
    $fill
      ? `
    flex: 1 0 auto;
    align-items: center;
  `
      : ''}

  ${media.max(820)} {
    padding: 36px 0 56px;
  }
`;

export const LoginPageCard = styled.div`
  width: 100%;
  max-width: 440px;
  background-color: ${({ theme }) => theme.custom.white};
  border-radius: 20px;
  padding: 32px 28px;
  box-sizing: border-box;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${media.max(820)} {
    padding: 28px 20px;
  }
`;

export const LoginPageIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: center;
`;

export const LoginPageTitle = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 500;
  line-height: 1.35;
  color: ${({ theme }) => theme.palette.text.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily};

  ${media.max(820)} {
    font-size: 20px;
  }
`;

export const LoginPageFootnote = styled.p`
  margin: 0;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const LoginPageFootnoteLink = styled.a`
  color: ${({ theme }) => theme.palette.primary.main};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export const LoginPageSubtitle = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.55;
  color: ${({ theme }) => theme.palette.text.secondary};

  ${media.max(820)} {
    font-size: 15px;
  }
`;
